import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SearchCommandPalette from '../../components/ui/SearchCommandPalette';
import { NotificationProvider, useNotification } from '../../components/ui/NotificationToast';
import { citiesService, subUnionsService } from '../../services/dataService';
import Icon from '../../components/AppIcon';


// Import page components
import CitiesToolbar from './components/CitiesToolbar';
import CitiesTableHeader from './components/CitiesTableHeader';
import CityTableRow from './components/CityTableRow';
import CityFormModal from './components/CityFormModal';
import MapPreviewPanel from './components/MapPreviewPanel';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

const CitiesManagementContent = () => {
  const { success, error, warning } = useNotification();
  
  // UI State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMapCollapsed, setIsMapCollapsed] = useState(true);
  
  // Data State
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  
  // Form State
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [cityToDelete, setCityToDelete] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  
  // Filter & Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentLanguage, setCurrentLanguage] = useState('ar');
  
  // Loading State
  const [isLoading, setIsLoading] = useState(true);
  const [isFormLoading, setIsFormLoading] = useState(false);

  // Fetch cities and calculate subUnionCount from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch cities and sub-unions in parallel
        const [citiesData, subUnionsData] = await Promise.all([
          citiesService.getAll(),
          subUnionsService.getAll()
        ]);

        // Calculate subUnionCount for each city
        const citiesWithCounts = citiesData?.map(city => {
          const subUnionCount = subUnionsData?.filter(
            union => union?.cityId === city?.id
          )?.length || 0;

          return {
            id: city?.id,
            nameAr: city?.nameAr,
            nameEn: city?.nameEn,
            nameTr: city?.nameTr,
            svgX: city?.svgX || 0,
            svgY: city?.svgY || 0,
            colorZone: typeof city?.colorZone === 'number' ? city?.colorZone : (parseInt(city?.colorZone) || 1),
            clickZone: city?.clickZone || '',
            subUnionCount: subUnionCount,
            isActive: true, // Default to active, can be updated if needed
            createdAt: city?.createdAt || new Date().toISOString()
          };
        });

        setCities(citiesWithCounts);
        setFilteredCities(citiesWithCounts);
      } catch (err) {
        console.error('Error fetching cities:', err);
        error('فشل في تحميل المدن. يرجى المحاولة مرة أخرى.');
        setCities([]);
        setFilteredCities([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [error]);

  // Filter cities based on search and language
  useEffect(() => {
    let filtered = cities;

    if (searchQuery?.trim()) {
      filtered = filtered?.filter(city => {
        const searchLower = searchQuery?.toLowerCase();
        return (city?.nameAr?.toLowerCase()?.includes(searchLower) ||
        (city?.nameEn && city?.nameEn?.toLowerCase()?.includes(searchLower)) || (city?.nameTr && city?.nameTr?.toLowerCase()?.includes(searchLower)));
      });
    }

    // Sort cities
    filtered?.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'name':
          aValue = currentLanguage === 'en' ? (a?.nameEn || a?.nameAr) :
                   currentLanguage === 'tr' ? (a?.nameTr || a?.nameAr) : a?.nameAr;
          bValue = currentLanguage === 'en' ? (b?.nameEn || b?.nameAr) :
                   currentLanguage === 'tr' ? (b?.nameTr || b?.nameAr) : b?.nameAr;
          break;
        case 'subUnionCount':
          aValue = a?.subUnionCount;
          bValue = b?.subUnionCount;
          break;
        case 'coordinates':
          aValue = a?.svgX + a?.svgY;
          bValue = b?.svgX + b?.svgY;
          break;
        case 'status':
          aValue = a?.isActive ? 1 : 0;
          bValue = b?.isActive ? 1 : 0;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        default:
          aValue = a?.nameAr;
          bValue = b?.nameAr;
      }

      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredCities(filtered);
  }, [cities, searchQuery, sortField, sortDirection, currentLanguage]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'k') {
        e?.preventDefault();
        setIsSearchOpen(true);
      }
      if (e?.key === 'Escape') {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Event Handlers
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleCitySelect = (cityId, isSelected) => {
    setSelectedCities(prev => 
      isSelected 
        ? [...prev, cityId]
        : prev?.filter(id => id !== cityId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedCities(isSelected ? filteredCities?.map(city => city?.id) : []);
  };

  const handleAddCity = () => {
    setEditingCity(null);
    setIsFormModalOpen(true);
  };

  const handleEditCity = (city) => {
    setEditingCity(city);
    setIsFormModalOpen(true);
  };

  const handleDeleteCity = (city) => {
    setCityToDelete(city);
    setIsBulkDelete(false);
    setIsDeleteModalOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedCities?.length === 0) {
      warning('يرجى تحديد مدن للحذف');
      return;
    }
    setIsBulkDelete(true);
    setIsDeleteModalOpen(true);
  };

  const handleSaveCity = async (cityData) => {
    setIsFormLoading(true);
    
    try {
      if (editingCity) {
        // Update existing city
        await citiesService.update(editingCity.id, {
          nameAr: cityData.nameAr,
          nameEn: cityData.nameEn,
          nameTr: cityData.nameTr,
          svgX: cityData.svgX,
          svgY: cityData.svgY,
          colorZone: cityData.colorZone,
          clickZone: cityData.clickZone?.trim() || cityData.nameAr?.trim() || 'default',
          cityCode: cityData.cityCode || ''
        });
        
        // Refresh data to get updated subUnionCount
        const [citiesData, subUnionsData] = await Promise.all([
          citiesService.getAll(),
          subUnionsService.getAll()
        ]);
        
        const citiesWithCounts = citiesData?.map(city => {
          const subUnionCount = subUnionsData?.filter(
            union => union?.cityId === city?.id
          )?.length || 0;
          return {
            id: city?.id,
            nameAr: city?.nameAr,
            nameEn: city?.nameEn,
            nameTr: city?.nameTr,
            svgX: city?.svgX || 0,
            svgY: city?.svgY || 0,
            colorZone: typeof city?.colorZone === 'number' ? city?.colorZone : (parseInt(city?.colorZone) || 1),
            clickZone: city?.clickZone || '',
            subUnionCount: subUnionCount,
            isActive: true,
            createdAt: city?.createdAt || new Date().toISOString()
          };
        });
        
        setCities(citiesWithCounts);
        setFilteredCities(citiesWithCounts);
        success('تم تحديث المدينة بنجاح');
      } else {
        // Add new city
        await citiesService.create({
          nameAr: cityData.nameAr,
          nameEn: cityData.nameEn,
          nameTr: cityData.nameTr,
          svgX: cityData.svgX,
          svgY: cityData.svgY,
          colorZone: cityData.colorZone,
          clickZone: cityData.clickZone?.trim() || 10,
          cityCode: cityData.nameAr
        });
        
        // Refresh data
        const [citiesData, subUnionsData] = await Promise.all([
          citiesService.getAll(),
          subUnionsService.getAll()
        ]);
        
        const citiesWithCounts = citiesData?.map(city => {
          const subUnionCount = subUnionsData?.filter(
            union => union?.cityId === city?.id
          )?.length || 0;
          return {
            id: city?.id,
            nameAr: city?.nameAr,
            nameEn: city?.nameEn,
            nameTr: city?.nameTr,
            svgX: city?.svgX || 0,
            svgY: city?.svgY || 0,
            colorZone: typeof city?.colorZone === 'number' ? city?.colorZone : (parseInt(city?.colorZone) || 1),
            clickZone: city?.clickZone || '',
            subUnionCount: subUnionCount,
            isActive: true,
            createdAt: city?.createdAt || new Date().toISOString()
          };
        });
        
        setCities(citiesWithCounts);
        setFilteredCities(citiesWithCounts);
        success('تم إضافة المدينة بنجاح');
      }
      
      setIsFormModalOpen(false);
      setEditingCity(null);
    } catch (err) {
      console.error('Error saving city:', err);
      error('حدث خطأ أثناء حفظ المدينة');
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsFormLoading(true);
    
    try {
      if (isBulkDelete) {
        // Delete multiple cities
        await Promise.all(selectedCities.map(id => citiesService.delete(id)));
        const count = selectedCities.length;
        setSelectedCities([]);
        
        // Refresh data
        const [citiesData, subUnionsData] = await Promise.all([
          citiesService.getAll(),
          subUnionsService.getAll()
        ]);
        
        const citiesWithCounts = citiesData?.map(city => {
          const subUnionCount = subUnionsData?.filter(
            union => union?.cityId === city?.id
          )?.length || 0;
          return {
            id: city?.id,
            nameAr: city?.nameAr,
            nameEn: city?.nameEn,
            nameTr: city?.nameTr,
            svgX: city?.svgX || 0,
            svgY: city?.svgY || 0,
            colorZone: typeof city?.colorZone === 'number' ? city?.colorZone : (parseInt(city?.colorZone) || 1),
            clickZone: city?.clickZone || '',
            subUnionCount: subUnionCount,
            isActive: true,
            createdAt: city?.createdAt || new Date().toISOString()
          };
        });
        
        setCities(citiesWithCounts);
        setFilteredCities(citiesWithCounts);
        success(`تم حذف ${count} مدينة بنجاح`);
      } else if (cityToDelete) {
        // Delete single city
        await citiesService.delete(cityToDelete.id);
        
        // Refresh data
        const [citiesData, subUnionsData] = await Promise.all([
          citiesService.getAll(),
          subUnionsService.getAll()
        ]);
        
        const citiesWithCounts = citiesData?.map(city => {
          const subUnionCount = subUnionsData?.filter(
            union => union?.cityId === city?.id
          )?.length || 0;
          return {
            id: city?.id,
            nameAr: city?.nameAr,
            nameEn: city?.nameEn,
            nameTr: city?.nameTr,
            svgX: city?.svgX || 0,
            svgY: city?.svgY || 0,
            colorZone: typeof city?.colorZone === 'number' ? city?.colorZone : (parseInt(city?.colorZone) || 1),
            clickZone: city?.clickZone || '',
            subUnionCount: subUnionCount,
            isActive: true,
            createdAt: city?.createdAt || new Date().toISOString()
          };
        });
        
        setCities(citiesWithCounts);
        setFilteredCities(citiesWithCounts);
        success('تم حذف المدينة بنجاح');
      }
      
      setIsDeleteModalOpen(false);
      setCityToDelete(null);
      setIsBulkDelete(false);
    } catch (err) {
      console.error('Error deleting city:', err);
      error('حدث خطأ أثناء حذف المدينة');
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleViewOnMap = (city) => {
    setSelectedCity(city);
    setIsMapCollapsed(false);
  };

  const handleExport = () => {
    const dataToExport = selectedCities?.length > 0 
      ? cities?.filter(city => selectedCities?.includes(city?.id))
      : cities;
    
    const csvContent = [
      ['الاسم العربي', 'الاسم الإنجليزي', 'الاسم التركي', 'إحداثي X', 'إحداثي Y', 'منطقة اللون', 'الاتحادات الفرعية', 'الحالة'],
      ...dataToExport?.map(city => [
        city?.nameAr,
        city?.nameEn || '',
        city?.nameTr || '',
        city?.svgX,
        city?.svgY,
        city?.colorZone,
        city?.subUnionCount,
        city?.isActive ? 'نشطة' : 'غير نشطة'
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cities-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    
    success('تم تصدير البيانات بنجاح');
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = (e) => {
      const file = e?.target?.files?.[0];
      if (file) {
        success('تم استيراد الملف بنجاح');
      }
    };
    input?.click();
  };

  const activeCount = cities?.filter(city => city?.isActive)?.length;

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Header 
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen}
      />
      <div className="flex">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
        
        <main className={`flex-1 transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        } mt-16`}>
          <div className="p-6">
            {/* Breadcrumbs */}
            <Breadcrumbs className="mb-6" />
            
            {/* Toolbar */}
            <CitiesToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedCount={selectedCities?.length}
              onAddCity={handleAddCity}
              onBulkDelete={handleBulkDelete}
              onExport={handleExport}
              onImport={handleImport}
              currentLanguage={currentLanguage}
              onLanguageChange={setCurrentLanguage}
              totalCount={cities?.length}
              activeCount={activeCount}
            />

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Cities Table */}
              <div className="lg:col-span-2">
                <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
                  {isLoading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                      <p className="text-muted-foreground">جاري تحميل المدن...</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <CitiesTableHeader
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                          selectedCount={selectedCities?.length}
                          totalCount={filteredCities?.length}
                          onSelectAll={handleSelectAll}
                          currentLanguage={currentLanguage}
                        />
                        <tbody>
                          {filteredCities?.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="px-4 py-8 text-center">
                                <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">
                                  {searchQuery ? 'لا توجد مدن تطابق البحث' : 'لا توجد مدن'}
                                </p>
                              </td>
                            </tr>
                          ) : (
                            filteredCities?.map((city) => (
                              <CityTableRow
                                key={city?.id}
                                city={city}
                                isSelected={selectedCities?.includes(city?.id)}
                                onSelect={handleCitySelect}
                                onEdit={handleEditCity}
                                onDelete={handleDeleteCity}
                                onViewMap={handleViewOnMap}
                                currentLanguage={currentLanguage}
                              />
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Map Panel */}
              <div className="lg:col-span-1">
                <MapPreviewPanel
                  cities={cities}
                  selectedCity={selectedCity}
                  onCitySelect={setSelectedCity}
                  isCollapsed={isMapCollapsed}
                  onToggleCollapse={() => setIsMapCollapsed(!isMapCollapsed)}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Modals */}
      <CityFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingCity(null);
        }}
        onSave={handleSaveCity}
        city={editingCity}
        isLoading={isFormLoading}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setCityToDelete(null);
          setIsBulkDelete(false);
        }}
        onConfirm={handleConfirmDelete}
        city={cityToDelete}
        isLoading={isFormLoading}
        isBulkDelete={isBulkDelete}
        selectedCount={selectedCities?.length}
      />
      <SearchCommandPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
      <QuickActionButton />
    </div>
  );
};

const CitiesManagement = () => {
  return (
    <NotificationProvider>
      <CitiesManagementContent />
    </NotificationProvider>
  );
};

export default CitiesManagement;